<template>
  <div class="space-y-6">
    <p class="text-body text-gray-600 dark:text-gray-300">
      Add photos to showcase your work and build trust with potential customers. You can organize them by category.
    </p>

    <!-- Category tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <nav class="-mb-px flex gap-4">
        <button
          v-for="cat in categories"
          :key="cat.value"
          type="button"
          class="whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors"
          :class="activeCategory === cat.value
            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'"
          @click="activeCategory = cat.value">
          {{ cat.label }}
          <span
            v-if="getCategoryCount(cat.value) > 0"
            class="ml-2 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs">
            {{ getCategoryCount(cat.value) }}
          </span>
        </button>
      </nav>
    </div>

    <!-- Upload area -->
    <div
      class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 p-6 text-center transition-colors"
      :class="{ 'border-primary-500 bg-primary-50 dark:bg-primary-900/20': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop">
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        class="hidden"
        @change="handleFileSelect">

      <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>

      <p class="mt-4 text-body text-gray-600 dark:text-gray-300">
        <button
          type="button"
          class="text-primary-600 dark:text-primary-400 hover:text-primary-700 font-medium"
          @click="$refs.fileInput.click()">
          Click to upload
        </button>
        or drag and drop
      </p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        PNG, JPG up to 10MB each
      </p>

      <!-- Upload progress -->
      <div v-if="uploading" class="mt-4">
        <div class="progress">
          <div class="progress-bar" :style="{ width: `${uploadProgress}%` }" />
        </div>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Uploading... {{ uploadProgress }}%
        </p>
      </div>
    </div>

    <!-- Images grid -->
    <div v-if="filteredImages.length > 0" class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
      <div
        v-for="image in filteredImages"
        :key="image.id"
        class="group relative aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
        <img
          :src="image.thumbnail_url || image.image_url"
          :alt="image.title || 'Gallery image'"
          class="h-full w-full object-cover">

        <!-- Hover overlay -->
        <div class="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            class="rounded-full bg-white dark:bg-gray-900 p-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="editImage(image)">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            type="button"
            class="rounded-full bg-white dark:bg-gray-900 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
            @click="handleDeleteImage(image.id)">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>

        <!-- Featured badge -->
        <div
          v-if="image.featured"
          class="absolute left-2 top-2">
          <span class="badge badge-primary text-xs">Featured</span>
        </div>
      </div>
    </div>

    <!-- Empty state for category -->
    <div
      v-else
      class="rounded-lg bg-gray-50 dark:bg-gray-900 p-8 text-center">
      <p class="text-body text-gray-600 dark:text-gray-300">
        No {{ activeCategory }} photos yet
      </p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Upload some photos to get started
      </p>
    </div>

    <!-- Edit image modal -->
    <div
      v-if="editingImage"
      class="fixed inset-0 flex items-center justify-center"
      style="z-index: var(--z-modal);">
      <div class="absolute inset-0" style="background-color: rgb(var(--color-gray-900-rgb) / 0.5);" @click="editingImage = null" />
      <div class="relative w-full max-w-md rounded-xl p-6" style="background-color: var(--bg-surface); box-shadow: var(--shadow-xl);">
        <h3 class="heading-4 mb-4" style="color: var(--text-primary);">
          Edit Photo
        </h3>

        <div class="space-y-4">
          <!-- Preview -->
          <div class="aspect-video overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
            <img
              :src="editingImage.image_url"
              :alt="editingImage.title"
              class="h-full w-full object-contain">
          </div>

          <!-- Title -->
          <div>
            <label for="imageTitle" class="form-label">Title</label>
            <input
              id="imageTitle"
              v-model="editForm.title"
              type="text"
              class="form-input"
              placeholder="e.g., Kitchen Remodel">
          </div>

          <!-- Description -->
          <div>
            <label for="imageDescription" class="form-label">Description</label>
            <textarea
              id="imageDescription"
              v-model="editForm.description"
              rows="2"
              class="form-textarea"
              placeholder="Optional description..." />
          </div>

          <!-- Category -->
          <div>
            <label for="imageCategory" class="form-label">Category</label>
            <div class="form-select-wrapper">
              <select
                id="imageCategory"
                v-model="editForm.category"
                class="form-select">
                <option
                  v-for="cat in categories"
                  :key="cat.value"
                  :value="cat.value">
                  {{ cat.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Featured toggle -->
          <div class="flex items-center gap-2">
            <input
              id="imageFeatured"
              v-model="editForm.featured"
              type="checkbox"
              class="h-4 w-4 rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-500">
            <label for="imageFeatured" class="text-sm text-gray-700 dark:text-gray-200">
              Featured image (shown prominently on website)
            </label>
          </div>
        </div>

        <!-- Modal actions -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="btn btn-secondary"
            @click="editingImage = null">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="savingImage"
            @click="saveImageEdit">
            {{ savingImage ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'

const props = defineProps({
  wizard: { type: Object, required: true },
  mapKey: { type: String, required: true }
})

const categories = [
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'team', label: 'Team' },
  { value: 'project', label: 'Projects' },
  { value: 'hero', label: 'Hero/Banner' },
  { value: 'general', label: 'General' }
]

const activeCategory = ref('portfolio')
const isDragging = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const editingImage = ref(null)
const savingImage = ref(false)

const editForm = reactive({
  title: '',
  description: '',
  category: '',
  featured: false
})

const filteredImages = computed(() => props.wizard.galleryImages.value.filter((img) => img.category === activeCategory.value))

function getCategoryCount(category) {
  return props.wizard.galleryImages.value.filter((img) => img.category === category).length
}

function handleFileSelect(event) {
  const files = Array.from(event.target.files || [])
  if (files.length > 0) {
    uploadFiles(files)
  }
  event.target.value = ''
}

function handleDrop(event) {
  isDragging.value = false
  const files = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith('image/'))
  if (files.length > 0) {
    uploadFiles(files)
  }
}

async function uploadFiles(files) {
  // Validate file sizes (10MB max each)
  const oversizedFiles = files.filter((f) => f.size > 10 * 1024 * 1024)
  if (oversizedFiles.length > 0) {
    alert(`${oversizedFiles.length} file(s) exceed the 10MB limit and will be skipped.`)
    files = files.filter((f) => f.size <= 10 * 1024 * 1024)
  }

  if (files.length === 0) return

  uploading.value = true
  uploadProgress.value = 0

  try {
    await props.wizard.uploadGalleryImages(
      files,
      activeCategory.value,
      (progress) => { uploadProgress.value = progress }
    )
  } catch (err) {
    console.error('Failed to upload images:', err)
    alert('Failed to upload some images. Please try again.')
  } finally {
    uploading.value = false
    uploadProgress.value = 0
  }
}

function editImage(image) {
  editingImage.value = image
  editForm.title = image.title || ''
  editForm.description = image.description || ''
  editForm.category = image.category
  editForm.featured = image.featured || false
}

async function saveImageEdit() {
  if (!editingImage.value) return

  savingImage.value = true

  try {
    await props.wizard.updateGalleryImage(editingImage.value.id, {
      title: editForm.title,
      description: editForm.description,
      category: editForm.category,
      featured: editForm.featured
    })
    editingImage.value = null
  } catch (err) {
    console.error('Failed to update image:', err)
    alert('Failed to update image. Please try again.')
  } finally {
    savingImage.value = false
  }
}

async function handleDeleteImage(imageId) {
  if (!confirm('Are you sure you want to delete this image?')) return

  try {
    await props.wizard.removeGalleryImage(imageId)
  } catch (err) {
    console.error('Failed to delete image:', err)
    alert('Failed to delete image. Please try again.')
  }
}
</script>
